import React, { useState } from "react";
import { RotateCcw } from "lucide-react";
import dayjs, { Dayjs } from "dayjs";
import toast from "react-hot-toast";
import { DatePicker, Select, Button, TextArea, Input } from "../../common";
import { formatTimeOnly } from "../../../utils/timeUtils";
import { useGetMasterConfigByCategoryQuery } from "../../../store/apis/masterConfig.api";
import { MasterConfigCategory } from "../../../constants";
import type { SelectOption } from "../../common/Select";

interface GameBookingFormProps {
  users: SelectOption[];
  currentUserId: string;
  onSubmit: (data: {
    gameId: string;
    startTime: string;
    duration: number;
    participants: string[];
    notes: string;
  }) => Promise<void>;
  submitting: boolean;
}

const DURATION_OPTIONS = [
  { label: "15 min", value: "15" },
  { label: "30 min", value: "30" },
  { label: "45 min", value: "45" },
  { label: "60 min", value: "60" },
];

const GameBookingForm: React.FC<GameBookingFormProps> = ({
  users,
  currentUserId,
  onSubmit,
  submitting,
}) => {
  const { data: games = [] } = useGetMasterConfigByCategoryQuery(
    MasterConfigCategory.GAME_TYPE,
  );

  const [formData, setFormData] = useState({
    gameId: "",
    date: "" as string,
    time: null as Dayjs | null,
    duration: "30",
    participants: [] as string[],
    notes: "",
  });

  const gameOptions = games
    .filter((g) => g.isActive)
    .map((g) => ({ label: g.displayName, value: g.id }));

  const handleReset = () => {
    setFormData({
      gameId: "",
      date: "",
      time: null,
      duration: "30",
      participants: [],
      notes: "",
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.gameId) {
      toast.error("Please select a game.");
      return;
    }
    if (!formData.date) {
      toast.error("Please select a date.");
      return;
    }
    if (!formData.time) {
      toast.error("Please select a start time.");
      return;
    }

    const datePart = formData.date;
    const timePart = formData.time.format("HH:mm");
    const startTime = dayjs(`${datePart} ${timePart}`).toISOString();

    try {
      await onSubmit({
        gameId: formData.gameId,
        startTime,
        duration: Number(formData.duration),
        participants: formData.participants.filter((p) => p !== currentUserId),
        notes: formData.notes,
      });
      handleReset();
    } catch {
      // Form keeps its values on failure so the user can retry
    }
  };

  const handleDateChange = (value: Dayjs | null) => {
    setFormData((prev) => ({
      ...prev,
      date: value ? value.format("YYYY-MM-DD") : "",
    }));
  };

  return (
    <div className="bg-white rounded-[9px] border border-border-secondary shadow-xs mt-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4 px-4 sm:px-[22px] py-3.5 sm:py-[18px] border-b border-border-tertiary">
        <div className="text-[15px] sm:text-heading-sm font-bold text-text-primary">
          Create Game Booking
        </div>
      </div>

      <form className="space-y-4 p-6" onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <Select
              label="Game"
              required
              value={formData.gameId}
              onChange={(val) =>
                setFormData((prev) => ({
                  ...prev,
                  gameId: val as string,
                }))
              }
              options={gameOptions}
              placeholder="Select game"
              className="w-full"
              searchable
            />
          </div>

          <div>
            <DatePicker
              label="Date"
              required
              value={formData.date ? dayjs(formData.date) : null}
              onChange={handleDateChange}
              placeholder="Select date"
              className="w-full"
              format="DD/MM/YYYY"
              allowClear={false}
              minDate={dayjs().startOf("day")}
            />
          </div>

          <div>
            <Input
              type="time"
              label="Start Time"
              required
              value={formData.time ? formData.time.format("HH:mm") : ""}
              onChange={(val) => {
                if (!val) {
                  setFormData((prev) => ({ ...prev, time: null }));
                  return;
                }
                const [hours, minutes] = String(val).split(":");
                const newTime = dayjs().hour(Number(hours)).minute(Number(minutes)).second(0);
                setFormData((prev) => ({ ...prev, time: newTime }));
              }}
              className="w-full bg-transparent"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <Select
              label="Duration"
              value={formData.duration}
              onChange={(val) =>
                setFormData((prev) => ({
                  ...prev,
                  duration: val as string,
                }))
              }
              options={DURATION_OPTIONS}
              placeholder="Select duration"
              className="w-full mt-2"
            />
          </div>

          <div className="md:col-span-2">
            <Select
              label="Participants"
              value={formData.participants}
              onChange={(vals) =>
                setFormData((prev) => ({
                  ...prev,
                  participants: vals as string[],
                }))
              }
              options={users}
              placeholder="Select participants"
              className="w-full mt-2"
              searchable
              multiple
            />
          </div>
        </div>

        <div>
          <TextArea
            label="Notes"
            value={formData.notes}
            onChange={(value) => setFormData((prev) => ({ ...prev, notes: value }))}
            placeholder="Optional notes for the booking..."
            style={{ backgroundColor: "transparent" }}
          />
        </div>

        <div className="flex items-center gap-3 max-xxs:gap-2 justify-end">
          <Button
            htmlType="button"
            appearance="secondary"
            size="middle"
            className="max-xxs:!px-3 max-xxs:!py-1.5 max-xxs:!text-xs max-xxs:!h-auto max-xxs:!rounded-md"
            onClick={handleReset}
            icon={<RotateCcw className="w-4 h-4 max-xxs:w-3.5 max-xxs:h-3.5" />}
          >
            Reset
          </Button>
          <Button
            htmlType="submit"
            appearance="primary"
            size="middle"
            className="max-xxs:!px-3 max-xxs:!py-1.5 max-xxs:!text-xs max-xxs:!h-auto max-xxs:!rounded-md !bg-gradient-to-r !from-btn-brand-start !to-btn-brand-end !text-white hover:!shadow-btn-action hover:!translate-y-[-1px] transition-all"
            loading={submitting}
          >
            {submitting ? "Booking..." : "Book Slot"}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default GameBookingForm;
